import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useToast,
} from '@chakra-ui/react';
import PageCard from 'components/page-card';
import Card from 'components/card/Card';
import { useParams } from 'react-router-dom';
import Loader from '../../../components/Loader';
import { translate } from '../../../i18n';
import {
    CampaignIncentiveType,
    CampaignStatus,
} from '../../../models/campaign/dto/campaign.dto';
import { EXEC_STATUS_COLORS, formatConversionRate, formatDate, RULE_TYPE_LABELS, STATUS_COLORS } from '../utils/campaign.utils';
import { useGetCampaign } from '../hooks/useGetCampaign';
import { useGetCampaignAnalytics } from '../hooks/useGetCampaignAnalytics';
import { useGetCampaignExecutions } from '../hooks/useGetCampaignExecutions';
import { usePreviewAudience } from '../hooks/usePreviewAudience';
import { useUpdateCampaign } from '../hooks/useUpdateCampaign';
import ErrorState from '../../../components/states/ErrorState';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { READ_FAILED_I18N_KEY } from '../../_shared/readState';
import { typography } from 'theme/tokens';

export default function CampaignDetailPage() {
    const { campaignId } = useParams<{ campaignId: string }>();
    const toast = useToast();
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    const { data: campaign, isLoading, isError } = useGetCampaign(campaignId || '');
    const { data: executions, isLoading: executionsLoading, isError: executionsError } = useGetCampaignExecutions(campaignId || '');
    // The audience preview is a MANUAL read: `usePreviewAudience`'s `enabled`
    // defaults to false, this page passes false, and the only thing that issues
    // the request is the "Preview audience" button below calling `refetch`.
    // Dropping `isError` therefore cost more here than it does on an automatic
    // read — the operator explicitly asked a question, and a refused answer made
    // the button stop spinning and render exactly what it rendered before, so
    // the action appeared to do nothing at all. It also withholds, with no
    // notice, the overlapping-campaign warnings rendered under it, which are the
    // last thing shown before a campaign is sent.
    //
    // `classifyRead` is deliberately NOT used for this one, and the reason is
    // the opposite of ceremony: its `unreadable` state is `isError && !data`
    // (R96-3, so a failed BACKGROUND refresh keeps a good value on screen),
    // which excludes precisely the case a manual read must report — the
    // operator pressed the button again, the refetch was refused, and the count
    // beside the label is the PREVIOUS answer. Keying the notice on the raw
    // `isError` says "we asked and did not get an answer" in both worlds, and
    // the retained count is still shown rather than blanked, so R96-3's
    // guarantee is kept by the render rather than by the classifier.
    const { data: audiencePreview, refetch: refetchAudience, isFetching: previewLoading, isError: previewError } = usePreviewAudience(campaignId || '', false);
    const { mutateAsync: updateCampaign, isLoading: isUpdating } = useUpdateCampaign();
    const { data: analytics, isError: analyticsError } = useGetCampaignAnalytics(campaignId || '');

    const handleStatusChange = async (newStatus: CampaignStatus) => {
        if (!campaignId) return;
        try {
            await updateCampaign({ id: campaignId, input: { status: newStatus } });
            toast({
                title: translate('campaign.updated_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error: any) {
            toast({
                title: translate('campaign.update_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    if (isLoading) {
        return <Loader isLoading={true} />;
    }

    if (isError || !campaign) {
        return (
            <PageCard showBackButton title={translate('campaign.campaigns')}>
                <ErrorState message={translate('campaign.error_loading')} />
            </PageCard>
        );
    }

    return (
        <Box>
            <PageCard
                showBackButton
                title={campaign.name}
                rightHeader={
                    <Badge colorScheme={STATUS_COLORS[campaign.status]} fontSize='md' px={3} py={1}>
                        {campaign.status}
                    </Badge>
                }
            >
                <Box p='20px'>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <Box>
                        <Text fontWeight='600' mb={1}>{translate('campaign.notification_title')}</Text>
                        <Text color={textColor}>{campaign.notificationTitle}</Text>
                    </Box>
                    <Box>
                        <Text fontWeight='600' mb={1}>{translate('campaign.notification_body')}</Text>
                        <Text color={textColor}>{campaign.notificationBody}</Text>
                    </Box>
                    <Box>
                        <Text fontWeight='600' mb={1}>{translate('campaign.schedule')}</Text>
                        <Text color={textColor}>
                            {campaign.schedule.frequency} @ {campaign.schedule.hour}:00 ({campaign.schedule.timezone})
                            {campaign.schedule.dayOfWeek && ` — ${translate('campaign.day')} ${campaign.schedule.dayOfWeek}`}
                            {campaign.schedule.runAt && ` — ${formatDate(campaign.schedule.runAt)}`}
                        </Text>
                    </Box>
                    <Box>
                        <Text fontWeight='600' mb={1}>{translate('campaign.next_run')}</Text>
                        <Text color={textColor}>
                            {campaign.nextRunAt ? formatDate(campaign.nextRunAt) : '-'}
                        </Text>
                    </Box>
                    <Box>
                        <Text fontWeight='600' mb={1}>{translate('campaign.last_run')}</Text>
                        <Text color={textColor}>
                            {campaign.lastRunAt ? formatDate(campaign.lastRunAt) : '-'}
                        </Text>
                    </Box>
                    <Box>
                        <Text fontWeight='600' mb={1}>{translate('campaign.rules')}</Text>
                        <Flex gap={1} wrap="wrap">
                            {campaign.rules.map((rule) => (
                                <Badge key={rule.id} colorScheme="brand">
                                    {RULE_TYPE_LABELS[rule.type]}: {rule.value} {translate('campaign.days')}
                                </Badge>
                            ))}
                        </Flex>
                    </Box>
                    {campaign.incentiveType !== CampaignIncentiveType.NONE && (
                        <Box>
                            <Text fontWeight='600' mb={1}>{translate('campaign.incentive')}</Text>
                            <Badge colorScheme="purple">
                                {campaign.incentiveType === CampaignIncentiveType.COUPON
                                    ? translate('campaign.incentive_coupon')
                                    : `${translate('campaign.incentive_credits')}: ${campaign.creditAmount}`}
                            </Badge>
                        </Box>
                    )}
                </SimpleGrid>

                <Flex gap={2} wrap="wrap">
                    {campaign.status === CampaignStatus.DRAFT && (
                        <Button
                            colorScheme="green"
                            size="sm"
                            onClick={() => handleStatusChange(CampaignStatus.ACTIVE)}
                            isLoading={isUpdating}
                        >
                            {translate('campaign.activate')}
                        </Button>
                    )}
                    {campaign.status === CampaignStatus.ACTIVE && (
                        <Button
                            colorScheme="yellow"
                            size="sm"
                            onClick={() => handleStatusChange(CampaignStatus.PAUSED)}
                            isLoading={isUpdating}
                        >
                            {translate('campaign.pause')}
                        </Button>
                    )}
                    {campaign.status === CampaignStatus.PAUSED && (
                        <Button
                            colorScheme="green"
                            size="sm"
                            onClick={() => handleStatusChange(CampaignStatus.ACTIVE)}
                            isLoading={isUpdating}
                        >
                            {translate('campaign.resume')}
                        </Button>
                    )}
                    {campaign.status !== CampaignStatus.ARCHIVED && (
                        <Button
                            colorScheme="red"
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusChange(CampaignStatus.ARCHIVED)}
                            isLoading={isUpdating}
                        >
                            {translate('campaign.archive')}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        onClick={() => refetchAudience()}
                        isLoading={previewLoading}
                    >
                        {translate('campaign.preview_audience')}
                        {audiencePreview && ` (${audiencePreview.count})`}
                    </Button>
                </Flex>

                {previewError && (
                    <Box mt={4}>
                        <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
                    </Box>
                )}

                {audiencePreview?.overlappingCampaigns && audiencePreview.overlappingCampaigns.length > 0 && (
                    <Box mt={4}>
                        {audiencePreview.overlappingCampaigns.map((overlap) => (
                            <Alert key={overlap.campaignId} status="warning" mb={2} borderRadius="md">
                                <AlertIcon />
                                {translate('campaign.users_also_in')
                                    .replace('{count}', String(overlap.overlapCount))
                                    .replace('{name}', overlap.campaignName)}
                            </Alert>
                        ))}
                    </Box>
                )}
                </Box>
            </PageCard>

            <Card p='20px' mb={6}>
                <Text color={textColor} fontSize='18px' fontWeight='700' mb={4}>
                    {translate('campaign.analytics')}
                </Text>
                {analyticsError ? (
                    <ErrorState message={translate('campaign.error_loading')} />
                ) : analytics ? (
                    <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
                        <Stat>
                            <StatLabel>{translate('campaign.total_sent')}</StatLabel>
                            <StatNumber>{analytics.totalSent}</StatNumber>
                        </Stat>
                        <Stat>
                            <StatLabel>{translate('campaign.total_opened')}</StatLabel>
                            <StatNumber>{analytics.totalOpened}</StatNumber>
                        </Stat>
                        <Stat>
                            <StatLabel>{translate('campaign.total_conversions')}</StatLabel>
                            <StatNumber>{analytics.totalConversions}</StatNumber>
                        </Stat>
                        <Stat>
                            <StatLabel>{translate('campaign.conversion_rate')}</StatLabel>
                            <StatNumber>{formatConversionRate(analytics.conversionRate)}</StatNumber>
                        </Stat>
                        <Stat>
                            <StatLabel>{translate('campaign.coupon_redeemed')}</StatLabel>
                            <StatNumber>{analytics.totalCouponRedeemed}</StatNumber>
                        </Stat>
                        <Stat>
                            <StatLabel>{translate('campaign.total_failed')}</StatLabel>
                            <StatNumber color={analytics.totalFailed > 0 ? 'red.500' : undefined}>
                                {analytics.totalFailed}
                            </StatNumber>
                        </Stat>
                    </SimpleGrid>
                ) : (
                    <Loader isLoading={true} variant="spinner" />
                )}
            </Card>

            <Card p='20px'>
                <Text color={textColor} fontSize='18px' fontWeight='700' mb={4}>
                    {translate('campaign.execution_history')}
                </Text>
                {executionsLoading ? (
                    <Loader isLoading={true} variant="spinner" />
                ) : executionsError ? (
                    <ErrorState message={translate('campaign.error_loading')} />
                ) : (
                    <Table variant='simple' color='gray.500'>
                        <Thead>
                            <Tr>
                                <Th borderColor={borderColor}>{translate('campaign.date')}</Th>
                                <Th borderColor={borderColor}>{translate('campaign.status')}</Th>
                                <Th borderColor={borderColor}>{translate('campaign.audience_size')}</Th>
                                <Th borderColor={borderColor}>{translate('campaign.sent')}</Th>
                                <Th borderColor={borderColor}>{translate('campaign.failed')}</Th>
                                <Th borderColor={borderColor}>{translate('campaign.opened')}</Th>
                                <Th borderColor={borderColor}>{translate('campaign.converted')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {executions?.map((exec) => (
                                <Tr key={exec.id}>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize='sm' color={textColor}>
                                            {formatDate(exec.createdAt)}
                                        </Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Badge colorScheme={EXEC_STATUS_COLORS[exec.status]}>
                                            {exec.status}
                                        </Badge>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize='sm' color={textColor}>{exec.audienceSize}</Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize='sm' color={textColor}>{exec.sentCount}</Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize='sm' color={exec.failedCount > 0 ? 'red.500' : textColor}>
                                            {exec.failedCount}
                                        </Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize='sm' color={textColor}>{exec.openedCount ?? 0}</Text>
                                    </Td>
                                    <Td borderColor={borderColor}>
                                        <Text fontSize='sm' color={textColor}>{exec.conversionCount ?? 0}</Text>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
                {(!executions || executions.length === 0) && !executionsLoading && !executionsError && (
                    <Flex justify="center" py={8}>
                        <Text color="gray.400">{translate('campaign.no_executions')}</Text>
                    </Flex>
                )}
            </Card>
        </Box>
    );
}
